import API from '../config/api';
import React, { useState, useRef, useEffect } from 'react';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate, Link } from 'react-router-dom';

import { Card, Button, Spinner } from 'react-bootstrap';

import CustomNavbar from '../components/CustomNavbar';
import { authStyles } from './ForgotPasswordPage';

const OTP_LENGTH = 6;

function VerifyOtpPage() {
  const navigate = useNavigate();

  const email = localStorage.getItem('resetEmail') || '';

  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const inputsRef = useRef([]);

  useEffect(() => {
    /* Nudge the user back if they reached this page without an email */
    if (!email) {
      toast.warning('Please request an OTP first');
      navigate('/forgot-password');
      return;
    }

    inputsRef.current[0]?.focus();
  }, [email, navigate]);

  /* Mask the email: jo***@gmail.com */
  const maskedEmail = email.replace(/^(.{2})(.*)(@.*)$/, (_, a, b, c) => {
    return `${a}${'*'.repeat(Math.max(b.length, 1))}${c}`;
  });

  const handleChange = (index, value) => {
    const char = value.replace(/\D/g, '').slice(-1);

    const next = [...digits];
    next[index] = char;
    setDigits(next);

    if (char && index < OTP_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);

    if (!pasted) return;

    const next = Array(OTP_LENGTH).fill('');
    pasted.split('').forEach((d, i) => (next[i] = d));
    setDigits(next);

    const focusIndex = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIndex]?.focus();
  };

  const verifyOtp = async (e) => {
    e?.preventDefault();

    const otp = digits.join('');

    if (otp.length < OTP_LENGTH) {
      toast.warning('Please enter the complete OTP');
      return;
    }

    try {
      setLoading(true);

      await axios.post(`${API}/users/verify-otp`, {
        email,
        otp
      });

      toast.success('OTP verified');

      navigate('/reset-password');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    try {
      setResending(true);

      await axios.post(`${API}/users/forgot-password`, {
        email
      });

      setDigits(Array(OTP_LENGTH).fill(''));
      inputsRef.current[0]?.focus();

      toast.success('A new OTP has been sent');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResending(false);
    }
  };

  return (
    <>
      <CustomNavbar />

      <div className="auth-wrap">
        <Card className="border-0 shadow-lg auth-card">
          <Card.Body className="p-4 p-md-5">
            <div className="text-center mb-4">
              <div className="auth-icon">📩</div>

              <h3 className="fw-bold mb-2">Verify OTP</h3>

              <p className="text-muted mb-0">
                We've sent a {OTP_LENGTH}-digit code to
                {maskedEmail ? (
                  <>
                    {' '}
                    <span className="fw-semibold text-dark">{maskedEmail}</span>
                  </>
                ) : (
                  ' your email'
                )}
                . Enter it below to continue.
              </p>
            </div>

            <form onSubmit={verifyOtp}>
              <div className="otp-inputs" onPaste={handlePaste}>
                {digits.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputsRef.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    className="otp-box"
                    value={digit}
                    onChange={(e) => handleChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                  />
                ))}
              </div>

              <Button
                type="submit"
                className="w-100 fw-bold auth-btn mt-4"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify OTP'
                )}
              </Button>
            </form>

            <div className="text-center mt-4">
              <span className="text-muted">Didn't receive the code? </span>

              <button
                type="button"
                className="btn btn-link p-0 auth-link align-baseline"
                onClick={resendOtp}
                disabled={resending}
              >
                {resending ? 'Resending...' : 'Resend OTP'}
              </button>
            </div>

            <div className="text-center mt-2">
              <Link to="/forgot-password" className="auth-link">
                ← Change Email
              </Link>
            </div>
          </Card.Body>
        </Card>
      </div>

      <style>{authStyles}</style>
    </>
  );
}

export default VerifyOtpPage;
